import { useEffect, useRef, type RefObject } from 'react';
import gsap from 'gsap';

/**
 * Bewegung im Dashboard.
 *
 * Grundsatz aus dem Design-System: Animation muss eine Ursache-Wirkung zeigen,
 * nicht dekorieren. Deshalb nur zwei Muster: Eintritt von unten (neue Ebene)
 * und gestaffelte Listen (Reihenfolge wird sichtbar).
 *
 * prefers-reduced-motion wird respektiert, und zwar indem die Animation gar
 * nicht erst läuft. Ein "kuerzer" reicht nicht, wenn jemand Bewegung aus
 * medizinischen Gruenden abgestellt hat.
 */

export function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

/** Eintritt eines Bereichs, z. B. nach einem Seitenwechsel. */
export function useEnter<T extends HTMLElement>(deps: unknown[] = []): RefObject<T | null> {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || prefersReducedMotion()) return;
    const animation = gsap.fromTo(
      element,
      { opacity: 0, y: 12 },
      { opacity: 1, y: 0, duration: 0.32, ease: 'power2.out', clearProps: 'transform' },
    );
    return () => {
      animation.kill();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return ref;
}

/** Gestaffelte Liste. 40ms pro Element, wie im Material-Motion-Leitfaden. */
export function useStagger<T extends HTMLElement>(selector: string, deps: unknown[] = []): RefObject<T | null> {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || prefersReducedMotion()) return;
    const items = element.querySelectorAll(selector);
    if (!items.length) return;
    const animation = gsap.fromTo(
      items,
      { opacity: 0, y: 14 },
      {
        opacity: 1,
        y: 0,
        duration: 0.34,
        ease: 'power2.out',
        stagger: 0.04,
        clearProps: 'transform',
      },
    );
    return () => {
      animation.kill();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return ref;
}

/** Zahl hochzaehlen, wenn sich ein Wert aendert. Nur für Kennzahlen. */
export function animateNumber(element: HTMLElement | null, to: number, format: (value: number) => string): void {
  if (!element) return;
  if (prefersReducedMotion()) {
    element.textContent = format(to);
    return;
  }
  const state = { value: Number(element.dataset.value ?? 0) };
  gsap.to(state, {
    value: to,
    duration: 0.6,
    ease: 'power2.out',
    onUpdate: () => {
      element.textContent = format(state.value);
    },
    onComplete: () => {
      element.dataset.value = String(to);
      element.textContent = format(to);
    },
  });
}
